import * as React from 'react'
import { useListMaterialsInfiniteQuery } from '@web/gen/service/creativefabrica/social/v2'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'
import { useDebounce } from '@web/hooks/use-debounce'
import { IconClose, IconSell } from '@repo/design-system-cf/components/icons'
import * as Popover from 'primitives/popover'
import { useProjectDraft } from '../project-draft-context'
import { useCreatePostProjectDialog } from '../create-post-project-dialog-context'
import { SearchInput } from './search-input'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { tw } from 'utils/classnames'
import { decodeHtmlEntities } from 'utils/decode-html-entities'

interface Material {
  id: string
  name: string
}

const MAX_MATERIALS = 10

export const MaterialsInput = () => {
  const { materials, setMaterials } = useProjectDraft()
  const { openMobileView } = useCreatePostProjectDialog()
  const [searchText, setSearchText] = React.useState('')
  const debouncedSearch = useDebounce(searchText, 400)
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isFetching } =
    useListMaterialsInfiniteQuery({ text: debouncedSearch })

  // Extract materials from response - try multiple possible structures
  const listMaterials = React.useMemo(() => {
    // Don't show old results when a new search is pending
    if (isPending) {
      return []
    }

    if (!data?.pages) {
      return []
    }

    return data.pages.flatMap(page => {
      if (page?.data && Array.isArray(page.data)) {
        return page.data
      }
      return []
    })
  }, [data, isPending])

  // Track if we've already triggered a fetch to prevent duplicate calls
  const hasTriggeredFetchRef = React.useRef(false)

  // Infinite scroll: fetch next page when scrolling to bottom
  // Update options when scroll container ref becomes available
  const intersectionOptions = React.useMemo(
    () => ({
      root: scrollContainerRef.current,
      rootMargin: '50px',
      threshold: 0.1,
    }),
    // Recreate when open changes to pick up the ref after mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  )

  const [loadMoreRef, entry] = useIntersectionObserver<HTMLDivElement>(intersectionOptions)

  // Reset fetch trigger when search changes
  React.useEffect(() => {
    hasTriggeredFetchRef.current = false
  }, [debouncedSearch])

  React.useEffect(() => {
    // Reset the flag when intersection state changes or when we're not intersecting
    if (!entry?.isIntersecting) {
      hasTriggeredFetchRef.current = false
      return
    }

    // Only fetch if we haven't already triggered and all conditions are met
    if (
      open &&
      hasNextPage &&
      !isFetchingNextPage &&
      !hasTriggeredFetchRef.current &&
      scrollContainerRef.current
    ) {
      hasTriggeredFetchRef.current = true
      fetchNextPage()
    }
  }, [open, hasNextPage, isFetchingNextPage, entry?.isIntersecting, fetchNextPage])

  React.useEffect(() => {
    // Don't open popover if max is reached
    if (materials.length >= MAX_MATERIALS) {
      setOpen(false)
      return
    }

    if (document.activeElement === inputRef.current && searchText.trim()) {
      setOpen(true)
    }
  }, [searchText, materials.length])

  const handleInputFocus = () => {
    if (materials.length >= MAX_MATERIALS) {
      return // Don't open popover when max is reached
    }
    if (listMaterials.length > 0 && debouncedSearch.trim()) {
      setOpen(true)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (materials.length >= MAX_MATERIALS) {
      return // Prevent input when max is reached
    }
    setSearchText(e.target.value)
  }

  const handleMaterialClick = (material: Material) => {
    const wasSelected = materials.some(m => m.id === material.id)
    handleMaterialSelect(material)
    // Close popover after selecting a material (but not when deselecting)
    if (!wasSelected) {
      setOpen(false)
      // Clear search text after selection
      setSearchText('')
    }
  }

  const handleMaterialSelect = (material: Material) => {
    setMaterials(prev => {
      // If already selected, remove it
      if (prev.some(m => m.id === material.id)) {
        return prev.filter(m => m.id !== material.id)
      }
      // If at max, don't add
      if (prev.length >= MAX_MATERIALS) {
        return prev
      }
      // Add the material
      return [...prev, material]
    })
  }

  const handleRemoveMaterial = (materialId: string) => {
    setMaterials(prev => prev.filter(m => m.id !== materialId))
  }

  const highlightMatch = (text: string, search: string) => {
    if (!search.trim()) {
      return text
    }

    const lowerText = text.toLowerCase()
    const lowerSearch = search.toLowerCase()

    // Only highlight if the match starts at the beginning
    if (lowerText.startsWith(lowerSearch)) {
      const matchLength = search.length
      const matchedPart = text.substring(0, matchLength)
      const restPart = text.substring(matchLength)

      return (
        <>
          <span className="font-bold text-cf-grey-1000">{matchedPart}</span>
          {restPart}
        </>
      )
    }

    return text
  }

  return (
    <div>
      <div className="relative flex flex-col tablet:flex-row items-center justify-start">
        <label
          htmlFor="materials-input"
          className="text-12 font-semibold flex-shrink-0 text-cf-grey-800 tablet:w-24 w-full text-left"
        >
          Materials
        </label>
        {/* mobile trigger */}
        <div
          className={tw(
            'relative block mt-2 tablet:hidden rounded-2 text-14 w-full border py-1.5 border-cf-transparent-grey-1000-12 pl-8 pr-4 placeholder:text-cf-grey-500 outline-none ring-0',
            materials.length >= MAX_MATERIALS
              ? 'border-cf-transparent-grey-1000-8 cursor-not-allowed text-cf-grey-400'
              : 'hover:border-cf-transparent-grey-1000-20 active:border-cf-transparent-grey-1000-12 text-cf-grey-1000 cursor-pointer',
          )}
          onClick={() => {
            if (materials.length < MAX_MATERIALS) {
              openMobileView({ type: 'materials' })
            }
          }}
        >
          <div className="absolute left-3 top-[10px] !size-4 cursor-pointer text-[#4B5563]">
            <IconSell className="!size-4" />
          </div>
          <p className="text-cf-grey-600">Start typing to add materials</p>
        </div>

        {/* desktop popover */}
        <Popover.Root open={open} onOpenChange={handleOpenChange} focusBehaviour="none">
          <Popover.Trigger asChild>
            <div className="hidden tablet:block relative w-full ">
              <div className="absolute left-3 top-[10px] !size-4 cursor-pointer text-[#4B5563]">
                <IconSell className="!size-4" />
              </div>
              <input
                ref={inputRef}
                disabled={materials.length >= MAX_MATERIALS}
                className={tw(
                  'rounded-2 text-14 w-full border py-1.5 border-cf-transparent-grey-1000-12 pl-8 pr-4 placeholder:text-cf-grey-500 outline-none ring-0',
                  materials.length >= MAX_MATERIALS
                    ? 'opacity-50 cursor-not-allowed text-cf-grey-500 bg-cf-grey-50'
                    : 'hover:border-cf-transparent-grey-1000-20 active:border-cf-transparent-grey-1000-12 text-cf-grey-1000 cursor-text',
                )}
                placeholder="Start typing to add materials"
                value={searchText}
                onChange={handleInputChange}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                  }
                }}
                onFocus={handleInputFocus}
                onBlur={e => {
                  // Don't close if focus is moving to popover content
                  const relatedTarget = e.relatedTarget as HTMLElement
                  if (relatedTarget?.closest('[data-popover-content]')) {
                    e.preventDefault()
                    return
                  }
                  // Small delay to allow clicking on items
                  setTimeout(() => {
                    const activeElement = document.activeElement
                    if (
                      !activeElement?.closest('[data-popover-content]') &&
                      !activeElement?.closest('[data-popover-trigger]')
                    ) {
                      // Only close if search is empty
                      // oxlint-disable-next-line unicorn/no-lonely-if
                      if (!debouncedSearch.trim()) {
                        setOpen(false)
                      }
                    }
                  }, 200)
                }}
              />
            </div>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="start"
              className="bg-cf-white border border-cf-transparent-grey-1000-12 rounded-b-2 p-0 !max-h-none shadow-[0px_4px_8px_3px_rgba(19,19,21,0.16),_0px_1px_2px_0px_rgba(19,19,21,0.12)]"
              style={{
                zIndex: 10_001,
                maxHeight: 'none',
                width: 'var(--radix-popover-trigger-width)',
              }}
              sideOffset={-5}
              onOpenAutoFocus={e => e.preventDefault()}
            >
              {listMaterials.length > 0 ? (
                <div
                  ref={scrollContainerRef}
                  className="flex flex-col gap-1 py-2 max-h-[300px] overflow-y-auto overflow-x-hidden"
                  onWheel={e => {
                    // Allow wheel events to work for scrolling - don't prevent default
                    e.stopPropagation()
                  }}
                >
                  {listMaterials.map((material, index) => {
                    const materialId = material.id || `material-${index}`
                    const materialName = decodeHtmlEntities(material.name) || 'Unnamed Material'
                    const materialObj: Material = { id: materialId, name: materialName }
                    const isSelected = materials.some(m => m.id === materialId)
                    const isMaxReached = materials.length >= MAX_MATERIALS && !isSelected
                    return (
                      <button
                        key={materialId}
                        type="button"
                        onClick={() => handleMaterialClick(materialObj)}
                        disabled={isMaxReached}
                        className={`text-left py-1 px-4 transition-colors ${
                          // oxlint-disable-next-line no-nested-ternary
                          isSelected
                            ? 'text-cf-grey-400'
                            : isMaxReached
                              ? 'cursor-not-allowed'
                              : 'hover:bg-cf-purple-50'
                        }`}
                      >
                        {highlightMatch(materialName, debouncedSearch)}
                      </button>
                    )
                  })}
                  {/* Sentinel element for infinite scroll */}
                  {hasNextPage && !isPending ? (
                    <div ref={loadMoreRef} className="h-4 flex items-center justify-center py-2">
                      {isFetchingNextPage ? (
                        <div className="text-12 text-cf-grey-600">Loading more...</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="p-4 text-center text-cf-grey-600">
                  {debouncedSearch.trim() ? (
                    isPending || isFetching ? (
                      <div className="flex items-center justify-center gap-2">
                        <Spinner className="size-4 text-cf-purple-900" />
                        <span>Searching...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-cf-grey-600">
                        <p className="text-14 font-bold">{`"${debouncedSearch}"`}</p>
                        <p>is not on our list of materials yet. </p>
                        <p className="text-14 pt-4">Try searching for something else.</p>
                      </div>
                    )
                  ) : searchText.trim() ? (
                    <div className="flex items-center justify-center gap-2">
                      <Spinner className="size-4 text-cf-purple-900" />
                      <span>Searching...</span>
                    </div>
                  ) : (
                    'Start typing to search...'
                  )}
                </div>
              )}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
      {/* Selected materials tags */}
      {materials.length > 0 ? (
        <div className="tablet:ml-24 mt-2 flex-wrap gap-2 flex ">
          {materials.map(material => (
            <div
              key={material.id}
              className="flex items-center gap-1.5 w-fit border border-cf-purple-200 rounded-[4px] bg-cf-white py-1 pr-1 pl-3 text-12 text-cf-grey-800"
            >
              <span>{decodeHtmlEntities(material.name)}</span>
              <button
                type="button"
                className="p-0 rounded-full size-[18px] flex items-center justify-center hover:bg-cf-grey-100 active:bg-cf-grey-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cf-blue-700 focus-visible:ring-offset-1"
                onClick={() => handleRemoveMaterial(material.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleRemoveMaterial(material.id)
                  }
                }}
                aria-label={`Remove ${decodeHtmlEntities(material.name)}`}
              >
                <IconClose className="!size-3 text-cf-purple-900" />
              </button>
            </div>
          ))}
          <div className="py-1.5">
            {materials.length < MAX_MATERIALS ? (
              <p className="text-12 text-cf-grey-600"> {MAX_MATERIALS - materials.length} left</p>
            ) : (
              <p className="text-12 text-cf-grey-600"> All {MAX_MATERIALS} used</p>
            )}
          </div>
        </div>
      ) : null}
      {materials.length === 0 ? (
        <p className="text-12 ml-24 py-1 text-cf-grey-600 hidden tablet:block">
          {MAX_MATERIALS - materials.length} left
        </p>
      ) : null}
    </div>
  )
}

export interface MaterialsInputMobileContentProps {
  onClose: () => void
}

export function MaterialsInputMobileContent({ onClose }: MaterialsInputMobileContentProps) {
  const [searchText, setSearchText] = React.useState('')
  const debouncedSearch = useDebounce(searchText, 400)

  const { materials, setMaterials } = useProjectDraft()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isFetching } =
    useListMaterialsInfiniteQuery({ text: debouncedSearch }, {})

  const listMaterials = React.useMemo(() => {
    // Don't show old results when a new search is pending
    if (isPending) {
      return []
    }

    if (!data?.pages) {
      return []
    }

    return data.pages.flatMap(page => {
      if (page?.data && Array.isArray(page.data)) {
        return page.data
      }
      return []
    })
  }, [data, isPending])

  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const node = loadMoreRef.current
    if (!node) {
      return
    }

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    })

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleMaterialSelect = (material: Material) => {
    setMaterials(prev => {
      // If already selected, remove it
      if (prev.some(m => m.id === material.id)) {
        return prev.filter(m => m.id !== material.id)
      }
      // If at max, don't add
      if (prev.length >= MAX_MATERIALS) {
        return prev
      }
      // Add the material
      return [...prev, material]
    })
    onClose()
  }

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] sm:h-auto">
      <div className="sticky top-0 z-20 bg-cf-white pb-3 px-4">
        <SearchInput
          className="w-full"
          value={searchText}
          onChange={setSearchText}
          placeholder="Search for a material"
        />
      </div>

      <div className="flex-1 overflow-y-auto pb-2 px-2">
        {listMaterials.length > 0 ? (
          <>
            {listMaterials.map((material, index) => {
              const materialId = material.id || `material-${index}`
              const materialName = decodeHtmlEntities(material.name) || 'Unnamed Material'
              const materialObj: Material = { id: materialId, name: materialName }
              const isSelected = materials.some(m => m.id === materialId)
              const isMaxReached = materials.length >= MAX_MATERIALS && !isSelected

              return (
                <div
                  key={materialId}
                  className={tw(
                    'flex flex-row items-center gap-3 mb-3 px-2 py-1',
                    isSelected || isMaxReached
                      ? 'opacity-40 cursor-not-allowed pointer-events-none'
                      : 'cursor-pointer hover:bg-cf-purple-50',
                  )}
                  onClick={() => {
                    if (isSelected || isMaxReached) {
                      return
                    }
                    handleMaterialSelect(materialObj)
                  }}
                >
                  <div>
                    <p className="text-cf-grey-800 font-semibold">{materialName}</p>
                  </div>
                </div>
              )
            })}
            <div
              ref={loadMoreRef}
              className="h-10 flex items-center justify-center text-cf-grey-500 text-xs"
            >
              {isFetchingNextPage ? <Spinner className="size-7 text-cf-purple-900" /> : ''}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            {debouncedSearch.trim() ? (
              isPending || isFetching ? (
                <div className="flex flex-col items-center justify-center gap-3">
                  <Spinner className="size-7 text-cf-purple-900" />
                  <p className="text-cf-grey-600">Searching...</p>
                </div>
              ) : (
                <p className="text-cf-grey-600 text-center">No materials found</p>
              )
            ) : (
              <p className="text-cf-grey-600 text-center">Start typing to search...</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
